import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2,
} from "lucide-react";
import PaymentMethodSelector from '@/components/payments/PaymentMethodSelector';
import ProgressIndicator from './components/ProgressIndicator';
import OrderSummary from './components/OrderSummary';
import ShipmentStep from './steps/ShipmentStep';
import ConsentCheckboxes from '@/components/consent/ConsentCheckboxes';

// Типы документов, требуемые на оформлении заказа.
// Опубликованные администратором версии этих типов будут показаны как чекбоксы.
const CHECKOUT_CONSENT_TYPES = ['agreement', 'privacy_policy'];



const initialFormData = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  payment_method: '',
  shipment_details: null,
  notes: '',
};

export default function CheckoutModal({ isOpen, onClose, cart, onUpdateQuantity, onComplete, total, lockedShipment }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1); // 1: Cart, 2: Shipping, 3: Payment
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  // Состояние согласий: какие типы документов сейчас отмечены и есть ли вообще документы.
  const [consentState, setConsentState] = useState({ allAccepted: false, acceptedTypes: [], types: [] });

  const totalWithShipping = useMemo(() => {
    const shipment = lockedShipment || formData.shipment_details;
    return total + (+shipment?.data?.price || 0);
  }, [total, formData.shipment_details, lockedShipment]);

  useEffect(() => {
    if (lockedShipment?.type === 'target_point') {
      setFormData((prev) => ({
        ...prev,
        shipment_details: lockedShipment
      }));
    }
  }, [lockedShipment]);

  const handleClose = () => {
    setCurrentStep(1);
    setFormData(initialFormData);
    onClose();
  };

  const validateEmail = useCallback((email) => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : t('checkout.customer_info.email_validation');
  }, [t]);

  const validatePhone = useCallback((phone) => {
    if (!phone) return '';
    const phoneRegex = /^\+[1-9]\d{7,14}$/;
    return phoneRegex.test(phone) ? '' : t('checkout.customer_info.phone_validation');
  }, [t]);

  const handleFieldChange = useCallback((field, value) => {
    // Очищаем ошибку при изменении поля
    setErrors(prev => ({ ...prev, [field]: '' }));
    setSubmitError('');

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
      return;
    }

    if (field === 'customer_phone') {
      const cleanedValue = value.replace(/[^\d+]/g, '');
      setFormData(prev => ({ ...prev, [field]: cleanedValue }));
      const phoneError = validatePhone(cleanedValue);
      if (phoneError) {
        setErrors(prev => ({ ...prev, [field]: phoneError }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Валидируем email в реальном времени
    if (field === 'customer_email') {
      const emailError = validateEmail(value);
      if (emailError) {
        setErrors(prev => ({ ...prev, [field]: emailError }));
      }
    }
  }, [validateEmail, validatePhone]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Финальная валидация перед отправкой
      const emailError = validateEmail(formData.customer_email);
      if (emailError) {
        setErrors(prev => ({ ...prev, customer_email: emailError }));
        setIsSubmitting(false);
        return;
      }

      const phoneError = validatePhone(formData.customer_phone);
      if (phoneError) {
        setErrors(prev => ({ ...prev, customer_phone: phoneError }));
        setIsSubmitting(false);
        return;
      }

      const consentPayload = consentState.types.length > 0
        ? {
            documentTypes: consentState.acceptedTypes,
            accepted: consentState.allAccepted,
            metadata: { source: 'checkout_modal' }
          }
        : null;

      await onComplete({ ...formData, consent: consentPayload });
      handleClose();
    } catch (error) {
      console.error('Error submitting order:', error);
      
      // Парсим ошибку и показываем дружелюбное сообщение
      let friendlyError = t('checkout.errors.order_creation');
      
      if (error.message) {
        if (error.message.includes('Invalid customer email format')) {
          friendlyError = t('checkout.errors.invalid_email');
        } else if (error.message.includes('product_id is required')) {
          friendlyError = t('checkout.errors.product_not_found');
        } else if (error.message.includes('quantity')) {
          friendlyError = t('checkout.errors.invalid_quantity');
        } else if (error.message.includes('not found')) {
          friendlyError = t('checkout.errors.product_unavailable');
        }
      }
      
      setSubmitError(friendlyError);
    }
    setIsSubmitting(false);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return cart.length > 0;
      case 2:
        if (!(formData.customer_name &&
              formData.customer_email &&
              !errors.customer_email &&
              formData.customer_phone &&
              !errors.customer_phone &&
              formData.shipment_details)) return false;
        // Согласие запрашивается на этом же шаге (ПД собираются здесь).
        // Если опубликованы документы — все галки обязательны.
        if (consentState.types.length > 0 && !consentState.allAccepted) return false;
        return true;
      case 3:
        return !!formData.payment_method;
      default:
        return false;
    }
  };

  const renderCartStep = () => (
    <div className="space-y-4">
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {cart.map((item) => (
          <div key={item.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
            {item.cover_image_url ? (
              <img 
                src={item.cover_image_url} 
                alt={item.name}
                className="w-16 h-16 object-cover rounded-md"
              />
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-md flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-amber-600" />
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-slate-900 truncate">{item.name}</h4>
              <p className="text-sm text-slate-600">₽{item.price.toLocaleString()} {t('checkout.cart.per_item')}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => onUpdateQuantity(item.id, item.cartQuantity - 1)}
                className="h-8 w-8"
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-slate-900 min-w-[2ch] text-center">
                {item.cartQuantity}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => onUpdateQuantity(item.id, item.cartQuantity + 1)}
                className="h-8 w-8"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="text-right">
              <p className="font-bold text-slate-900">₽{(item.price * item.cartQuantity).toLocaleString()}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onUpdateQuantity(item.id, 0)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      
      <Separator />
      
      <div className="flex justify-between items-center text-lg font-bold">
        <span>{t('checkout.cart.total')}</span>
        <span className="text-amber-600">₽{total.toLocaleString()}</span>
      </div>
    </div>
  );

  const renderShippingStep = () => (
    <div className="space-y-4">
      <ShipmentStep
        cart={cart}
        total={total}
        formData={formData}
        onFieldChange={handleFieldChange}
        errors={errors}
        lockedShipment={lockedShipment}
      />

      {/* Согласие на обработку ПД и оферту: ПД собираются на этом шаге,
          поэтому здесь же запрашиваем согласие (consent-spec.md §5.1).
          Без отметок «Продолжить» неактивна. Если документов в админке
          ещё не опубликовано — компонент полностью скрывается. */}
      <ConsentCheckboxes
        types={CHECKOUT_CONSENT_TYPES}
        onChange={setConsentState}
      />
    </div>
  );

  const renderPaymentStep = () => (
    <div className="space-y-4">
      <PaymentMethodSelector
        selectedMethod={formData.payment_method}
        onMethodSelect={(method) => handleFieldChange('payment_method', method)}
        currency="RUB"
        region="RU"
        amount={totalWithShipping}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('checkout.payment.order_comment_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.notes}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            placeholder={t('checkout.payment.order_comment_placeholder')}
            className="h-24"
          />
        </CardContent>
      </Card>

      {/* Order Summary */}
      <OrderSummary
        cart={cart}
        total={totalWithShipping}
        formData={formData}
      />
    </div>
  );

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return t('checkout.steps.cart');
      case 2: return t('checkout.steps.shipping');
      case 3: return t('checkout.steps.payment');
      default: return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {/* flex-col + ограничение по высоте — чтобы хедер и кнопки были
          приклеены, а скроллился только средний блок шага. */}
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col gap-3 bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900">
            {getStepTitle()}
          </DialogTitle>
        </DialogHeader>

        <ProgressIndicator currentStep={currentStep} totalSteps={3}/>

        <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 py-2">
          {currentStep === 1 && renderCartStep()}
          {currentStep === 2 && renderShippingStep()}
          {currentStep === 3 && renderPaymentStep()}
        </div>

        {submitError && (
          <Alert variant="destructive">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between items-center pt-3 border-t">
          <div>
            {currentStep > 1 && (
              <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                {t('checkout.buttons.back')}
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={handleClose}>
              {t('checkout.buttons.cancel')}
            </Button>
            {currentStep < 3 ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceed()}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {t('checkout.buttons.continue')}
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed() || isSubmitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSubmitting 
                  ? t('checkout.buttons.processing') 
                  : formData.payment_method === 'tbank' 
                    ? t('checkout.buttons.confirm_order_and_pay') 
                    : t('checkout.buttons.confirm_order')
                }
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
